/* eslint-disable @typescript-eslint/no-explicit-any */
import { FronteggNextJSSession } from '@frontegg/nextjs';
import { getSession } from '@frontegg/nextjs/pages';
import {
  CORRELATION_ID_HEADER,
  FLOW_ID_HEADER,
  GeneratedZorroPy,
  MonolithClientFactory,
  MonolithService,
  ZorroPyClientFactory,
} from '@zorro/clients';
import {
  COMMON_ROUTES,
  logger,
  setRequestUserContext,
  tryGetHeader,
} from '@zorro/common-utils';
import {
  GetServerSidePropsContext,
  GetServerSidePropsResult,
  NextApiRequest,
  NextApiResponse,
  PreviewData,
} from 'next/types';
import { ParsedUrlQuery } from 'node:querystring';
import { Logger } from 'pino';

export type ZorroNextJSSession = FronteggNextJSSession & { employerId: string };

/*
 * Maps the Frontegg session to the vendor-agnostic request user context so
 * the pino mixin stamps user identity on every log line of this request
 * (monolith customProps structure). Single mechanism: async context only.
 */
function stampSessionUserContext(session: FronteggNextJSSession) {
  setRequestUserContext({
    userId: session.user.sub,
    roles: session.user.roles.join(';'),
    permissions: session.user.permissions.join(';'),
    tenantId: session.user.tenantId,
  });
}

export async function withApi(
  request: NextApiRequest,
  _response: NextApiResponse,
  handler: (
    monolithClient: MonolithService,
    logger: Logger
  ) => Promise<void> | void
) {
  try {
    const monolithClient = MonolithClientFactory.getNextInstance(
      request.headers,
      undefined,
      tryGetHeader(request.headers, CORRELATION_ID_HEADER),
      tryGetHeader(request.headers, FLOW_ID_HEADER)
    );
    await handler(monolithClient, logger);
  } catch (error) {
    logger.error(error);
    throw error;
  }
}

export async function withApiSession(
  request: NextApiRequest,
  response: NextApiResponse,
  handler: (
    monolithClient: MonolithService,
    logger: Logger,
    session: ZorroNextJSSession
  ) => Promise<void> | void
) {
  const session = await getSession(request, response);

  if (!session) {
    response.status(401).json({ message: 'Unauthorized' });
    logger.error(
      {
        method: request.method,
        url: request.url,
        correlationId: tryGetHeader(request.headers, CORRELATION_ID_HEADER),
      },
      'Unauthorized'
    );
    return;
  }
  stampSessionUserContext(session);

  try {
    const monolithClient = MonolithClientFactory.getNextInstance(
      request.headers,
      session,
      tryGetHeader(request.headers, CORRELATION_ID_HEADER),
      tryGetHeader(request.headers, FLOW_ID_HEADER)
    );
    const zorroSession = {
      ...session,
      employerId: session.user.tenantId,
    };
    await handler(monolithClient, logger, zorroSession);
  } catch (error) {
    logger.error(error);
    throw error;
  }
}

export function withPageSession<
  Props extends { [key: string]: any } = { [key: string]: any },
  Params extends ParsedUrlQuery = ParsedUrlQuery,
  Preview extends PreviewData = PreviewData,
>(
  handler: (
    context: GetServerSidePropsContext<Params, Preview>,
    session: ZorroNextJSSession,
    monolithClient: MonolithService,
    zorroPyClient: GeneratedZorroPy.DefaultService,
    logger: Logger
  ) => Promise<GetServerSidePropsResult<Props>>
): (
  context: GetServerSidePropsContext<Params, Preview>
) => Promise<GetServerSidePropsResult<Props>> {
  return async (context) => {
    const session = await getSession(context.req, context.res);

    if (!session) {
      return {
        redirect: {
          destination: COMMON_ROUTES.LOGIN,
          permanent: false,
        },
      };
    }
    stampSessionUserContext(session);

    try {
      const monolithClient = MonolithClientFactory.getNextInstance(
        context.req.headers,
        session
      );
      const zorroPyClient = ZorroPyClientFactory.getInstance(session);
      const zorroSession = {
        ...session,
        employerId: session.user.tenantId,
      };
      return await handler(
        context,
        zorroSession,
        monolithClient,
        zorroPyClient,
        logger
      );
    } catch (error) {
      logger.error(error);
      throw error;
    }
  };
}
